import type { Runtime } from "@baton/core";
import { useSyncExternalStore } from "react";

/**
 * FR-015-155: the restore time the blocking notice names. Once set it stays set, because the only
 * way past the notice is a reload, which builds a new runtime.
 */
export interface RestoreNoticeStore {
  readonly restoredAt: string | null;
  show(restoredAt: string): void;
  subscribe(listener: () => void): () => void;
}

const stores = new WeakMap<Runtime, RestoreNoticeStore>();

export function restoreNoticeFor(runtime: Runtime): RestoreNoticeStore {
  const existing = stores.get(runtime);
  if (existing !== undefined) return existing;
  let restoredAt: string | null = null;
  const listeners = new Set<() => void>();
  const store: RestoreNoticeStore = {
    get restoredAt() {
      return restoredAt;
    },
    show(value) {
      restoredAt = value;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
  stores.set(runtime, store);
  return store;
}

export function useRestoredAt(runtime: Runtime): string | null {
  const store = restoreNoticeFor(runtime);
  return useSyncExternalStore((listener) => store.subscribe(listener), () => store.restoredAt);
}
